/**
 * When true, venue `copilotConfig.openRoleSwitching` is consulted:
 * - venue true  → staff can pick any venue role
 * - venue false → lock to allowlist / default role
 *
 * When false, always lock to the staff allowlist / default (ignore venue config).
 * Flip while reviewing with the team.
 */
export const HONOR_VENUE_OPEN_ROLE_SWITCHING = true;

/**
 * Resolves the staff venue assignment for the current venue.
 * Accepts camelCase / snake_case field names from the API.
 */
export function getStaffVenueAssignment(staff, venueId) {
	if (!staff) {
		return null;
	}

	const assignments = staff.venueAssignments || staff.venue_assignments || [];
	if (!assignments.length) {
		return null;
	}

	if (venueId != null && venueId !== '') {
		const match = assignments.find((assignment) => {
			const assignmentVenueId = assignment?.venueId ?? assignment?.venue_id ?? assignment?.venue?.id;
			return String(assignmentVenueId) === String(venueId);
		});
		if (match) {
			return match;
		}
	}

	return assignments.length === 1 ? assignments[0] : null;
}

/**
 * Normalizes role ids coming from the API (uuid, number, or hydra IRI).
 */
export function normalizeRoleId(value) {
	if (value == null || value === '') {
		return null;
	}
	if (typeof value === 'object') {
		const nested = value.id ?? value['@id'] ?? value.uuid;
		return normalizeRoleId(nested);
	}
	const asString = String(value);
	const iriMatch = asString.match(/\/([^/]+)\/?$/);
	return iriMatch ? iriMatch[1] : asString;
}

/**
 * Role ids the staff may select for this venue.
 * - Non-empty allowlist → those ids
 * - Empty allowlist + default role → only the default
 * - Otherwise → null (no restriction / show all)
 */
export function getStaffAllowedRoleIds(staff, venueId) {
	const assignment = getStaffVenueAssignment(staff, venueId);
	if (!assignment) {
		return null;
	}

	const rawAllowed = assignment.allowedCopilotRoleIds ?? assignment.allowed_copilot_role_ids ?? [];
	const allowedIds = (Array.isArray(rawAllowed) ? rawAllowed : []).map(normalizeRoleId).filter(Boolean);

	if (allowedIds.length) {
		return allowedIds;
	}

	const defaultRoleId = normalizeRoleId(assignment.defaultCopilotRoleId ?? assignment.default_copilot_role_id);
	if (defaultRoleId) {
		return [defaultRoleId];
	}

	return null;
}

/**
 * Filters venue roles for the logged-in staff.
 *
 * @param {object[]} roles
 * @param {object|null} staff
 * @param {string|number|null} venueId
 * @param {{ openRoleSwitching?: boolean }} [options] venue `copilotConfig.openRoleSwitching`
 */
export function filterRolesForStaff(roles, staff, venueId, options = {}) {
	const sourceRoles = Array.isArray(roles) ? roles : [];

	const venueAllowsOpenSwitching = options.openRoleSwitching !== false;
	if (HONOR_VENUE_OPEN_ROLE_SWITCHING && venueAllowsOpenSwitching) {
		return sourceRoles;
	}

	const allowedIds = getStaffAllowedRoleIds(staff, venueId);
	if (!allowedIds?.length) {
		return sourceRoles;
	}

	const allowedSet = new Set(allowedIds);
	return sourceRoles.filter((role) => allowedSet.has(normalizeRoleId(role?.id)));
}
